import { getAPIConfig } from '@tcp/core/src/utils';
import { getMultiPackStyleType } from '@tcp/core/src/components/features/CnC/AddedToBag/views/AddedToBag.utils';
import ADDEDTOBAG_CONSTANTS from '@tcp/core/src/components/features/CnC/AddedToBag/AddedToBag.constants';
import { getBreadCrumbs } from '@tcp/core/src/adobeAnalytics/adobeData.mapper';
import {
  TOP_RATED_ANALYTICS_STRING,
  BEST_SELLER_ANALYTICS_STRING,
  MOST_FAVORITED_ANALYTICS_STRING,
} from '@tcp/core/src/constants/badge.constants';

const apiConfigObj = getAPIConfig();
const conditionMap = {
  '0001': 'Single',
  '0002': 'Multipack',
  '0003': 'Set',
  '0005': 'Virtual Multipack',
  '0006': 'Virtual Set',
};
const TIME_ZONE = 'America/New_York';
const EN_US = 'en-US';

export const getProductBadge = (data) => {
  if (data?.isTopRated || data?.dimension108) {
    return TOP_RATED_ANALYTICS_STRING;
  }
  if (data?.isBestSeller || data?.dimension109) {
    return BEST_SELLER_ANALYTICS_STRING;
  }
  if (data?.isMostFavorite || data?.dimension139) {
    return MOST_FAVORITED_ANALYTICS_STRING;
  }

  return data?.esgCertified || '';
};

export const getProductBadgesAll = (data) => {
  const allBadgesArray = [];

  if (data?.isTopRated || data?.dimension108) {
    allBadgesArray.push(TOP_RATED_ANALYTICS_STRING);
  }
  if (data?.isBestSeller || data?.dimension109) {
    allBadgesArray.push(BEST_SELLER_ANALYTICS_STRING);
  }
  if (data?.isMostFavorite || data?.dimension139) {
    allBadgesArray.push(MOST_FAVORITED_ANALYTICS_STRING);
  }
  const zoneOneBadges = data?.zoneOneBadges || [];
  if (zoneOneBadges?.length) {
    for (let i = 0; i < zoneOneBadges.length; i += 1) {
      allBadgesArray.push(zoneOneBadges[i].label);
    }
  }
  if (allBadgesArray.length) {
    return allBadgesArray.join(',');
  }
  return data?.plpTopBadge || '';
};

const getProductOtherProps = (data, isProduct) => {
  let licensedProduct = data?.generalProductId || data?.uniqueId;
  if (isProduct) {
    licensedProduct = data?.id || data?.uniqueId;
  }
  return {
    isLicensed: data?.isLicensed === true ? 'yes' : '',
    licensedProduct: data?.isLicensed === true ? licensedProduct : '',
    licensedProductBrand: data?.licensedProductBrand || '',
    primaryCategoryID: data?.primaryCategory || '',
    primaryCategoryName: data?.primaryCategoryName || '',
  };
};

export function getProductDetailNClickJSON(data, isProduct) {
  const eventMethod = isProduct ? 'productDetailList' : 'productClickList';
  const newStore = [
    {
      SKU: data?.id || data?.uniqueId,
      name: data?.name,
      _id: data?.category || '',
      productListPosition: data?.position || '',
      productBrand: data?.brand || data?.primaryBrand,
      productSiteBrand: apiConfigObj?.brandId || '',
      productBadge: data?.dimension108 || data?.dimension109 || data?.plpTopBadge || '',
      productCurrentPrice: isProduct ? data?.offerPrice : data?.price,
      productOriginalPrice: data?.listPrice,
      productCrossSellDriver: data?.productCrossSellDriver || '',
      ...(isProduct && {
        productSize: '',
        productColor: data?.variant,
        productUID: '',
        productPriceVariance: (data?.listPrice - data?.offerPrice)?.toFixed(2),
        productPromotionalText: data?.dimension56,
        productAverageRating: data?.ratings,
        productPricingState: data?.dimension67,
        productMPack: data?.TCPStyleType,
        productMPackStyleType: conditionMap[data?.TCPStyleType] || null,
        productGender: '',
        ...getProductOtherProps(data, true),
        productBadge: getProductBadgesAll(data),
      }),
    },
  ];
  return {
    event: isProduct ? 'product-detail-view' : 'product-click',
    productInfo: {
      eventName: isProduct ? 'Product Detail View' : 'Product Clicks',
      [eventMethod]: JSON.stringify(newStore),
    },
  };
}

export function getProductImpressionJSON(data) {
  const pageViewValue = getBreadCrumbs(data?.pageView);
  const newStore = data?.productImpressions.map((item) => {
    const position = item?.position === 0 ? '0' : item?.position;
    return {
      SKU: item?.productPartNumber || item?.uniqueId || item?.colorId || item?.id || '',
      name: item?.name || '',
      productListPosition: position,
      _id: item?.category || item?.list || '',
      productBrand: item?.dimension35 || '',
      productSiteBrand: apiConfigObj?.brandId || '',
      productBadge: item?.productBadge,
      productCurrentPrice: item?.price || '',
      productOriginalPrice: item?.listPrice || '',
      productPriceVariance: (item?.listPrice - item?.price)?.toFixed(2) || '',
      productBreadCrumb: pageViewValue,
      eVar152: item?.shoppableImage152,
    };
  });

  return {
    event: 'product-impressions',
    productInfo: {
      eventName: 'Product Impressions',
      productImpressionsList: JSON.stringify(newStore),
    },
  };
}

export function getRemovedProductJSON(data) {
  const newStore = [
    {
      SKU: data?.productInfo?.productPartNumber,
      name: data?.itemInfo.name || '',
      _id: data?.category || '',
      productID: (data?.productInfo?.skuId || '')?.concat(
        '-',
        data?.productInfo?.productPartNumber || ''
      ),
      productBrand: data?.brand || '',
      productSiteBrand: apiConfigObj?.brandId || '',
      productSize: data?.itemInfo?.size || '',
      productColor: data?.itemInfo?.color || '',
      productUID: data?.productInfo?.skuId || '',
      productOriginalPrice: data?.itemInfo?.wasPrice || '',
      productCurrentPrice: data?.itemInfo?.offerPrice || '',
      productPriceVariance:
        (data?.itemInfo?.wasPrice - data?.itemInfo?.offerPrice)?.toFixed(2) || '',
      productAverageRating: '',
      productBadge: data?.miscInfo?.badge || '',
      productPricingState: '',
      productMPack: data?.productInfo?.tcpStyleQty || '',
      productMPackStyleType: conditionMap[data?.productInfo?.tcpStyleType] || '',
      productGender: '',
    },
  ];
  return {
    event: 'remove-from-cart',
    productInfo: {
      eventName: 'Remove From Cart',
      productRemoveFromCartList: JSON.stringify(newStore),
    },
  };
}

export function getProductCartViewJSON(data) {
  const newStore = data?.map((item) => {
    const itemValues = {
      SKU: item?.partNumber || '',
      name: item?.name || '',
      _id: '',
      productID: (item?.colorId || '')?.concat('-', item?.partNumber || ''),
      productBrand: item?.prodBrand || '',
      productSiteBrand: apiConfigObj?.brandId || '',
      productSize: item?.size || '',
      productColor: item?.color || '',
      productUID: item?.colorId || '',
      productPriceVariance: '',
      productPromotionalText: '',
      productAverageRating: '',
      productListPosition: '',
      productBadge: '',
      productPricingState: item?.pricingState || '',
      productMPack: '',
      productMPackStyleType: '',
      productGender: '',
      productCurrentPrice: item?.salePrice || '',
      productOriginalPrice: item?.wasPrice,
    };
    if (item?.dimension140) {
      itemValues.was_boss_unavailable = item?.dimension140;
    }
    return itemValues;
  });
  return {
    event: 'cart-view',
    productInfo: {
      eventName: 'Cart-View',
      productCartPageList: JSON.stringify(newStore),
    },
  };
}

export function getAddToWishListJSON(data) {
  const newStore = [
    {
      SKU: data?.colorProductId || '',
      name: '',
      _id: '',
      productID: '',
      productBrand: '',
      productSize: '',
      productColor: '',
      productUID: '',
      productCurrentPrice: '',
      productOriginalPrice: '',
      productPriceVariance: '',
      productAverageRating: '',
      productBadge: '',
      productPricingState: '',
      productMPack: '',
      productMPackStyleType: '',
      productGender: '',
    },
  ];
  return {
    event: 'add-to-wishlist',
    productInfo: {
      eventName: 'Add To Wish List',
      productAddToWishlist: JSON.stringify(newStore),
    },
  };
}

export function getRemoveFromWishlistJSON(data) {
  const newStore = [
    {
      SKU: data?.productId || data?.generalProductId || '',
      name: data?.name || '',
      _id: '',
      productBrand: '',
      productSize: '',
      productColor: '',
      productCurrentPrice: data?.offerPrice || '',
      productOriginalPrice: data?.listPrice || '',
      productPriceVariance: (data?.listPrice - data?.offerPrice)?.toFixed(2) || '',
      productAverageRating: '',
      productBadge: '',
      productPricingState: '',
      productMPack: '',
      productMPackStyleType: '',
      productGender: '',
    },
  ];
  return {
    event: 'remove-from-wishlist',
    productInfo: {
      eventName: 'Remove from Wish List',
      productRemoveFromWishlist: JSON.stringify(newStore),
    },
  };
}

export function getBagPageEditClickJSON(data) {
  const newStore = [
    {
      SKU: data?.partNumber || '',
      name: data?.name || '',
      _id: '',
      productID: `${data?.upc}-${data?.partNumber}` || '',
      productBrand: data?.prodBrand || '',
      productSize: data?.size || '',
      productColor: data?.color || '',
      productUID: data?.upc || '',
      productCurrentPrice: data?.price || '',
      productOriginalPrice: '',
      productPriceVariance: '',
      productAverageRating: '',
      productBadge: '',
      productPricingState: '',
      productMPack: '',
      productMPackStyleType: '',
      productGender: '',
    },
  ];
  return {
    event: 'bag-page-edit-clicks',
    productInfo: {
      eventName: 'BagPageEditClicks',
      productEditList: JSON.stringify(newStore),
    },
  };
}

export function getProductCheckoutJSON(data) {
  const newStore = data?.map((item) => {
    return {
      SKU: item?.partNumber || '',
      name: item?.name || '',
      _id: '',
      productID: item?.id || '',
      productBrand: item?.prodBrand || '',
      productSiteBrand: '',
      productSize: item?.size || '',
      productColor: item?.color || '',
      productUID: item?.ups || '',
      productCurrentPrice: item?.salePrice || '',
      productOriginalPrice: item?.wasPrice || '',
      productPriceVariance: (item?.wasPrice - item?.salePrice)?.toFixed(2) || '',
      productAverageRating: '',
      productListPosition: '',
      productPromotionText: '',
      productBadge: '',
      productPricingState: item?.pricingState || '',
      productMPack: '',
      productMPackStyleType: '',
      productGender: '',
      quantityCheckedOut: item?.quantity,
    };
  });
  return {
    event: 'checkout',
    productInfo: {
      eventName: 'Checkout',
      productCheckoutPageList: JSON.stringify(newStore),
    },
  };
}

export function getProductSaveForLaterJSON(data, isMethod) {
  const eventMethod = isMethod ? 'productSaveForLaterList' : 'productMoveToBagList';
  const newStore = {
    SKU: data?.partNumber,
    name: data?.name,
    _id: data?.category || '',
    productID: (data?.upc || '')?.concat('-', data?.partNumber || ''),
    productSiteBrand: apiConfigObj?.brandId,
  };
  if (isMethod) {
    newStore.saveForLaterAmount = data?.price;
  } else {
    newStore.moveToBagAmount = data?.price;
  }
  return {
    event: isMethod ? 'save-for-later-clicks' : 'move-to-bag',
    productInfo: {
      eventName: isMethod ? ' Save for Later Clicks' : 'Move to Bag',
      [eventMethod]: JSON.stringify([newStore]),
    },
  };
}
export function getCrossSellClicksJSON(data) {
  return {
    event: 'product-cross-sell-clicks',
    eventinfo: {
      eventName: 'Product Cross-Sell Clicks',
      monetateCarouselName: data,
    },
  };
}
export function getProductPurchaseConfirmationJSON(data, isMultipleOrder) {
  const productPurchaseList = isMultipleOrder ? 'productPurchaseList1' : 'productPurchaseList';
  const orderInfo = isMultipleOrder ? 'orderInfo1' : 'orderInfo';
  const purchaseTimestamp =
    new Date(new Date().toLocaleString(EN_US, { timeZone: TIME_ZONE })).getTime() / 1000;
  const newStore = {
    purchaseID: data?.actionField?.id,
    currencyCode: data?.currCode,
    couponCode: data?.actionField?.coupon,
    priceTotal: parseFloat(data?.actionField?.dimension73 ?? 0),
    giftCardAmountUsed: parseFloat(data?.actionField?.giftCardAmount ?? 0),
    discountAmount: parseFloat(data?.actionField?.metric7 ?? 0),
    taxAmount: parseFloat(data?.actionField?.taxAmount ?? 0),
    shippingAmount: parseFloat(data?.actionField?.shippingAmount ?? 0),
    paymentMethod: data?.actionField?.payment,
    shippingMethod: data?.productsObj?.dimension5,
    shippingZipCode: data?.actionField?.shippingZip,
    billingZipCode: data?.actionField?.billingZip,
    checkoutType: data?.actionField?.dimension98,
    cartType: data?.actionField?.dimension86,
    bagPagePickupOptions: '',
    expressPaymentOptions: '',
    promotionAmount: data?.actionField?.promotionAmount,
    eVar141: data?.actionField?.eVar141,
  };
  const newStore1 = data?.productsObj?.map((item) => {
    const itemValues = {
      SKU: item?.generalProductId || item?.dimension89 || item?.uniqueId,
      name: item?.name || '',
      _id: item?.category,
      productID: item?.productUID ? `${item?.productUID}-${item?.dimension89}` : item?.dimension89,
      productBrand: item?.dimension35,
      productSiteBrand: apiConfigObj?.brandId,
      productSize: item?.dimension88,
      productColor: item?.variant,
      productUID: item?.productUID,
      productCurrentPrice: (item?.price / item?.quantity).toFixed(2),
      productOriginalPrice: '',
      productPriceVariance: '',
      productAverageRating: '',
      productListPosition: '',
      productPromotionText: item?.dimension56,
      productBadge: item?.dimension108 || item?.dimension109 || '',
      productPricingState: '',
      productMPack: '',
      productMPackStyleType: '',
      productGender: '',
      quantity: item?.quantity,
      priceTotal: item?.price,
    };
    if (item?.dimension140) {
      itemValues.was_boss_unavailable = item?.dimension140;
    }
    return itemValues;
  });
  return {
    event: isMultipleOrder ? 'purchase1' : 'purchase',
    [orderInfo]: newStore,
    productInfo: {
      eventName: 'purchase',
      [productPurchaseList]: JSON.stringify(newStore1),
    },
    eventinfo: {
      eventName: 'Time between return and purchase',
      purchaseTimestamp,
    },
  };
}

export function getProductQuickViewJSON(data) {
  const newStore = [
    {
      SKU: data?.generalProductId || data?.uniqueId,
      name: data?.name || '',
      _id: data?.category,
      productID: '',
      productBrand: data?.brand || data?.primaryBrand,
      productSiteBrand: apiConfigObj?.brandId || '',
      productSize: '',
      productColor: data?.auxdescription || '',
      productUID: '',
      productCurrentPrice: data?.offerPrice,
      productOriginalPrice: data?.listPrice,
      productPriceVariance: (data?.listPrice - data?.offerPrice).toFixed(2) || '',
      productAverageRating: data?.ratings || '',
      productListPosition: '',
      productPromotionText: '',
      productBadge: getProductBadgesAll(data),
      productPricingState: data?.dimension67,
      productMPack: data?.tcpStyleQty || '',
      productMPackStyleType: conditionMap[data?.tcpStyleType] || '',
      productGender: '',
      productCrossSellDriver: data?.productCrossSellDriver || '',
      eVar152: data?.eVar152,
      ...getProductOtherProps(data),
    },
  ];
  return {
    event: 'product-quick-view',
    productInfo: {
      eventName: 'Product Quick View',
      productQuickViewList: JSON.stringify(newStore),
    },
  };
}

export function getPaymentBillingJson(data) {
  return {
    event: `${data}-click`,
    checkoutBillingInfo: {
      eventName: `${data} Click`,
    },
  };
}

export function getCheckoutBillingJson(data) {
  return {
    event: 'checkoutpage-billinginitiate',
    checkoutBillingInfo: {
      eventName: `Checkout Page - ${data} Billing Start`,
    },
  };
}

export function getBagPageBillingJson(data) {
  return {
    event: 'bagpage-billinginitiate',
    checkoutBillingInfo: {
      eventName: `Bag Page - ${data} Billing Start`,
    },
  };
}

export function getCheckoutStartJson(isMiniBag) {
  return {
    event: isMiniBag ? 'checkoutstart-minibag' : 'checkoutstart-bagpage',
    checkoutInfo: {
      eventName: isMiniBag ? 'Checkout Start Mini Bag' : 'Checkout Start Bag Page',
    },
  };
}

export const getCheckoutProducts = (productDetails) => {
  return productDetails?.map((data) => {
    const tempObj = {
      SKU: data?.partNumber,
      _id: '',
      productID: (data?.sku?.split('_')?.[0] || '')?.concat('-', data?.partNumber || ''),
      name: data?.name,
      productSiteBrand: getAPIConfig()?.brandId,
      productCurrentPrice: data?.salePrice,
      productSize: data?.size,
      productGender: '',
      productMPack: data?.tcpStyleQty
        ? `${data?.tcpStyleQty}${ADDEDTOBAG_CONSTANTS?.ADOBE_ANALYTICS?.PACK}`
        : '',
      productMPackStyleType: getMultiPackStyleType(data?.tcpStyleType) || '',
      productBadge: getProductBadge(data),
      productPromotionText: '',
      productOriginalPrice: data?.wasPrice,
      productPriceVariance: (data?.wasPrice - data?.salePrice).toFixed(2) || '',
      productAverageRating: '',
      productBrand: data?.prodBrand,
      productColor: data?.color,
      productUID: data?.sku?.split('_')?.[0],
      quantityCheckedOut: data?.quantity,
      crossSellDriver:
        data?.recsPageType || data?.recsType ? `${data?.recsPageType}-${data?.recsType}` : '',
    };
    if (data?.wasPrice && data?.salePrice) {
      tempObj.productPriceVariance = (data?.wasPrice - data?.salePrice)?.toFixed(2)?.toString();
    }
    if (data?.dimension140) {
      tempObj.was_boss_unavailable = data?.dimension140;
    }
    return tempObj;
  });
};
